import { useState, useEffect } from 'react';
import './admin.css';
import { useAccessControl } from '../../hooks/useAccessControl';

interface AdminProps {
  onNavigateToLogin?: () => void;
}

interface UserRoleRowProps {
  user: any;
  roles: any[];
  fetchUserRoles: (userId: string) => Promise<any[]>;
  assignRole: (userId: string, roleName: string) => Promise<void>;
  removeRole: (userId: string, roleName: string) => Promise<void>;
  setMessage: (message: { type: 'success' | 'error'; text: string } | null) => void;
}

const UserRoleRow = ({ user, roles, fetchUserRoles, assignRole, removeRole, setMessage }: UserRoleRowProps) => {
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadUserRoles();
  }, [user.user_id]);

  const loadUserRoles = async () => {
    const roles = await fetchUserRoles(user.user_id);
    setUserRoles(roles.map((r: any) => r.role_name));
  };

  const handleAssignRole = async () => {
    if (!selectedRole) {
      setMessage({ type: 'error', text: 'Please select a role' });
      return;
    }

    try {
      setIsLoading(true);
      await assignRole(user.user_id, selectedRole);
      await loadUserRoles();
      setSelectedRole('');
      setMessage({ type: 'success', text: `Role ${selectedRole} assigned to ${user.email}` });
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to assign role: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveRole = async (roleName: string) => {
    try {
      setIsLoading(true);
      await removeRole(user.user_id, roleName);
      await loadUserRoles();
      setMessage({ type: 'success', text: `Role ${roleName} removed from ${user.email}` });
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to remove role: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-table-row">
      <div className="admin-row-cell admin-row-email">
        <div className="email-badge">
          <span className="email-avatar">{user.email?.charAt(0).toUpperCase()}</span>
          <div className="email-info">
            <p className="email-text">{user.email}</p>
          </div>
        </div>
      </div>

      <div className="admin-row-cell admin-row-roles">
        {userRoles.length === 0 ? (
          <span className="role-empty">No roles assigned</span>
        ) : (
          <div className="roles-list">
            {userRoles.map((role) => (
              <span key={role} className="role-badge">
                <span className="role-name">{role}</span>
                <button
                  className="role-remove"
                  onClick={() => handleRemoveRole(role)}
                  disabled={isLoading}
                  aria-label={`Remove ${role} role`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="admin-row-cell admin-row-actions">
        <div className="role-assign-group">
          <select
            className="role-select"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            disabled={isLoading}
          >
            <option value="">Select role...</option>
            {roles.map((role) => (
              <option key={role.id} value={role.name}>
                {role.name}
              </option>
            ))}
          </select>
          <button
            className="btn-assign-role"
            onClick={handleAssignRole}
            disabled={!selectedRole || isLoading}
          >
            {isLoading ? '...' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Admin = ({ onNavigateToLogin }: AdminProps) => {
  const { isAdmin, users, roles, loadUsers, loadReferenceData, assignRole, removeRole, fetchUserRoles, isLoading } =
    useAccessControl();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    loadReferenceData();
    loadUsers();
  }, [isAdmin, loadReferenceData, loadUsers]);

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-not-found">
          <div className="not-found-icon">🔒</div>
          <h1 className="not-found-title">Access Denied</h1>
          <p className="not-found-subtitle">
            You don't have permission to access the admin panel
          </p>
          <button className="btn-primary-submit" onClick={onNavigateToLogin}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* Header */}
      <div className="admin-header">
        <h1 className="admin-title">🔐 Admin Dashboard</h1>
        <p className="admin-subtitle">Manage users and roles</p>
      </div>

      {/* Alert Messages */}
      {message && (
        <div className={`admin-alert alert-${message.type}`}>
          <span className="alert-icon">
            {message.type === 'success' ? '✓' : '✗'}
          </span>
          <span className="alert-text">{message.text}</span>
          <button
            className="alert-close"
            onClick={() => setMessage(null)}
            aria-label="Close alert"
          >
            ×
          </button>
        </div>
      )}

      {/* User Management Card */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">👥 User & Role Management</h2>
          <span className="admin-user-count">{users.length} users</span>
        </div>

        {isLoading && users.length === 0 ? (
          <div className="admin-loading">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <p className="admin-empty">No users found in the system</p>
        ) : (
          <div className="admin-table-container">
            {/* Table Header */}
            <div className="admin-table-header">
              <div className="admin-header-cell admin-header-email">User Email</div>
              <div className="admin-header-cell admin-header-roles">Current Roles</div>
              <div className="admin-header-cell admin-header-actions">Assign Role</div>
            </div>

            {/* Table Rows */}
            <div className="admin-table-body">
              {users.map((user) => (
                <UserRoleRow
                  key={user.user_id}
                  user={user}
                  roles={roles}
                  fetchUserRoles={fetchUserRoles}
                  assignRole={assignRole}
                  removeRole={removeRole}
                  setMessage={setMessage}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="admin-info-card">
        <div className="info-header">
          <h2 className="info-title">ℹ️ Admin Panel Features</h2>
        </div>
        <ul className="info-list">
          <li className="info-item">View all users in the system</li>
          <li className="info-item">Assign roles to users</li>
          <li className="info-item">Remove roles from users</li>
          <li className="info-item">Manage permissions matrix (in backend)</li>
        </ul>
      </div>
    </div>
  );
};
